import dayjs, {Dayjs} from "dayjs";
import {buildPublicRegistrationTheme} from "../../Theme/publicRegistrationTheme";
import {EventPublicAssetMode, normalizeEventPublicAssetMode} from "../../eventPublicAssetMode";
import type React from "react";
import {PayerTypeValue, PaymentMethodCategoryValue, PaymentMethodValue, PublicEventAssetLink, PublicEventFormResponse, PublicFormField, PublicFormFieldTypeName, PublicParticipationFeeOption, PublicPaymentMethodOptions, PublicRegistrationBulkRowValues, PublicRegistrationFormValues, PublicRegistrationLanguage, PublicRegistrationOption, PublicRegistrationOptionQuestionTypeName, PublicRoomAvailability, fieldTypeNames, paymentCategoryNameToValue, paymentMethodNameToValue} from "./types";

export const normalizePublicRegistrationLanguage = (value?: string | null): PublicRegistrationLanguage => {
    const normalized = value?.toLowerCase();

    if (normalized?.startsWith("en")) {
        return "en";
    }

    return "hr";
};

type PublicRegistrationUnavailableReason = "archived" | "completed" | "ended" | "form-inactive";

export const getPublicRegistrationUnavailableReason = (
    form: Pick<PublicEventFormResponse, "endDate" | "isArchived" | "lifecycleStatus" | "registrationUnavailableReason">,
    now: Dayjs = dayjs()
): PublicRegistrationUnavailableReason | null => {
    const serverReason = trimValue(form.registrationUnavailableReason).toLowerCase();
    if (serverReason === "archived" ||
        serverReason === "completed" ||
        serverReason === "ended" ||
        serverReason === "form-inactive") {
        return serverReason;
    }

    if (form.isArchived === true) {
        return "archived";
    }

    const normalizedLifecycleStatus = String(form.lifecycleStatus ?? "")
        .replace(/[_\s-]+/g, "")
        .toLowerCase();
    if (form.lifecycleStatus === 1 || normalizedLifecycleStatus === "completed") {
        return "completed";
    }

    if (form.lifecycleStatus === 2 || normalizedLifecycleStatus === "draft") {
        return "form-inactive";
    }

    const endDate = dayjs(form.endDate);
    if (endDate.isValid() && now.startOf("day").isAfter(endDate.startOf("day"))) {
        return "ended";
    }

    return null;
};

export const buildPublicEventFormUrl = (eventIdentifier: string): string =>
    `/api/public/events/${encodeURIComponent(eventIdentifier)}/form`;

export const buildPublicRegistrationSubmitUrl = (eventIdentifier: string): string =>
    `/api/public/events/${encodeURIComponent(eventIdentifier)}/registrations`;

export const buildPublicRegistrationProofUploadUrl = (eventIdentifier: string): string =>
    `/api/public/events/${encodeURIComponent(eventIdentifier)}/registrations/participant-type-proof`;

export const buildPublicRegistrationResendConfirmationUrl = (eventIdentifier: string, registrationId: number): string =>
    `/api/public/events/${encodeURIComponent(eventIdentifier)}/registrations/${registrationId}/resend-confirmation`;

export const buildPublicBulkRegistrationTemplateUrl = (eventIdentifier: string | number): string =>
    `/api/public/events/${encodeURIComponent(String(eventIdentifier))}/bulk-registration-template.csv`;

export const shouldUseEmbeddedLayout = (searchParams: URLSearchParams, insideFrame: boolean): boolean =>
    insideFrame || searchParams.get("embed") === "1" || searchParams.get("iframe") === "1";

export const normalizePublicFormFieldType = (value: string | number): PublicFormFieldTypeName => {
    if (typeof value === "number" && fieldTypeNames[value]) {
        return fieldTypeNames[value];
    }

    const normalized = String(value);
    return fieldTypeNames.find(name => name.toLowerCase() === normalized.toLowerCase()) ?? "Text";
};

export const normalizePublicRegistrationOptionQuestionType = (
    value: string | number | undefined | null
): PublicRegistrationOptionQuestionTypeName => {
    if (typeof value === "number") {
        return value === 1
            ? "SingleChoice"
            : value === 2
                ? "MultiChoice"
                : value === 3
                    ? "Text"
                    : "YesNo";
    }

    const normalized = String(value ?? "")
        .replace(/[_\s-]+/g, "")
        .toLowerCase();

    if (normalized === "singlechoice") {
        return "SingleChoice";
    }

    if (normalized === "multichoice") {
        return "MultiChoice";
    }

    if (normalized === "text") {
        return "Text";
    }

    return "YesNo";
};

export const detectInsideFrame = (): boolean => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};

export const trimValue = (value?: string | null): string => value?.trim() ?? "";
export const buildPublicBrandStyle = (form?: Pick<PublicEventFormResponse, "primaryColor" | "secondaryColor"> | null): React.CSSProperties =>
    buildPublicRegistrationTheme(form).style;

export const buildPublicEventAssetLinks = (
    form: PublicEventFormResponse,
    labels: Record<string, string>
): PublicEventAssetLink[] => {
    const assetMode = normalizeEventPublicAssetMode(form.publicAssetMode);
    const programPdfUrl = trimValue(form.programPdfUrl);
    const venueLayoutImageUrl = trimValue(form.venueLayoutImageUrl);

    if (assetMode === EventPublicAssetMode.CombinedPdf) {
        return programPdfUrl
            ? [{key: "combined", label: labels.combinedEventMaterials, href: programPdfUrl}]
            : [];
    }

    return [
        programPdfUrl ? {key: "program", label: labels.programPdf, href: programPdfUrl} : null,
        venueLayoutImageUrl ? {key: "venue-layout", label: labels.venueLayout, href: venueLayoutImageUrl} : null
    ].filter((item): item is PublicEventAssetLink => !!item);
};

export const replaceLabelValue = (template: string, name: string, value: string): string =>
    template.replace(`{${name}}`, value);

const looksLikeRawKey = (value: string): boolean =>
    /^[a-z0-9_.:-]+$/.test(value) && /[_:.-]/.test(value);

const humanizeRawKey = (value: string): string => {
    const words = value
        .replace(/^answer:/i, "")
        .replace(/[_:.-]+/g, " ")
        .trim();

    return words
        ? words.charAt(0).toUpperCase() + words.slice(1)
        : "";
};

export const formatPublicLabel = (label: string | null | undefined, fallback: string): string => {
    const trimmedLabel = label?.trim() ?? "";
    const trimmedFallback = fallback.trim();

    if (!trimmedLabel || trimmedLabel === trimmedFallback || looksLikeRawKey(trimmedLabel)) {
        return humanizeRawKey(trimmedFallback) || humanizeRawKey(trimmedLabel) || trimmedFallback;
    }

    return trimmedLabel;
};

export const getPublicEventName = (
    form: PublicEventFormResponse,
    language: PublicRegistrationLanguage
): string => {
    const localizedName = language === "en" ? form.eventNameEn : form.eventNameHr;
    return localizedName?.trim() || form.eventName?.trim() || String(form.eventId || form.eventCode);
};

export const isValidPublicRegistrationEmail = (value?: string | null): boolean => {
    const normalized = trimValue(value);
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
};

export const isValidPublicRegistrationPhone = (value?: string | null): boolean => {
    const normalized = trimValue(value);
    const digitCount = Array.from(normalized).filter(char => /\d/.test(char)).length;
    return digitCount >= 5 && /^[0-9+ \-()/]+$/.test(normalized);
};

export const isValidPublicRegistrationLinkedInUrl = (value?: string | null): boolean => {
    const normalized = trimValue(value);
    if (!normalized) {
        return true;
    }

    try {
        const url = new URL(normalized);
        const host = url.hostname.toLowerCase();
        return (url.protocol === "http:" || url.protocol === "https:") &&
            (host === "linkedin.com" || host.endsWith(".linkedin.com"));
    } catch {
        return false;
    }
};

const getDynamicAnswerValues = (field: PublicFormField, rawValue: unknown): string[] => {
    const fieldType = normalizePublicFormFieldType(field.type);

    if (fieldType === "MultiSelect") {
        if (Array.isArray(rawValue)) {
            return rawValue.map(value => String(value).trim()).filter(Boolean);
        }

        return String(rawValue ?? "")
            .split(/[,;\n\r]/)
            .map(value => value.trim())
            .filter(Boolean);
    }

    return [String(rawValue ?? "").trim()].filter(Boolean);
};

export const isDynamicAnswerValueMissing = (field: PublicFormField, rawValue: unknown): boolean => {
    const fieldType = normalizePublicFormFieldType(field.type);

    if (fieldType === "Checkbox") {
        return rawValue === undefined || rawValue === null;
    }

    if (fieldType === "MultiSelect") {
        return getDynamicAnswerValues(field, rawValue).length === 0;
    }

    if (fieldType === "Date") {
        return !formatDateOnly(rawValue as Dayjs | string | null);
    }

    return String(rawValue ?? "").trim() === "";
};

export const isDynamicAnswerValueCompatible = (field: PublicFormField, rawValue: unknown): boolean => {
    if (isDynamicAnswerValueMissing(field, rawValue)) {
        return true;
    }

    const fieldType = normalizePublicFormFieldType(field.type);
    const serializedValue = serializeAnswerValue(field, rawValue);

    switch (fieldType) {
    case "Number":
        return typeof rawValue === "number"
            ? Number.isFinite(rawValue)
            : Number.isFinite(Number(serializedValue));
    case "Date":
        return dayjs.isDayjs(rawValue)
            ? rawValue.isValid()
            : dayjs(serializedValue).isValid();
    case "Email":
        return isValidPublicRegistrationEmail(serializedValue);
    case "Phone":
        return isValidPublicRegistrationPhone(serializedValue);
    case "Checkbox":
        return typeof rawValue === "boolean" || ["true", "false"].includes(serializedValue.toLowerCase());
    default:
        return true;
    }
};

export const isDynamicAnswerValueAllowed = (field: PublicFormField, rawValue: unknown): boolean => {
    const fieldType = normalizePublicFormFieldType(field.type);
    if ((fieldType !== "Select" && fieldType !== "MultiSelect") || isDynamicAnswerValueMissing(field, rawValue)) {
        return true;
    }

    const allowedValues = new Set((field.options ?? [])
        .map(option => option.value?.trim().toLowerCase())
        .filter(Boolean));
    if (allowedValues.size === 0) {
        return false;
    }

    return getDynamicAnswerValues(field, rawValue)
        .every(value => allowedValues.has(value.toLowerCase()));
};

const enumNumber = <T extends number>(
    value: string | number,
    names: Record<string, T>,
    fallback: T
): T => {
    if (typeof value === "number") {
        return value as T;
    }

    const mapped = names[value];
    if (mapped !== undefined) {
        return mapped;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric as T : fallback;
};

export const normalizePaymentMethodValue = (value: string | number): PaymentMethodValue =>
    enumNumber(value, paymentMethodNameToValue, PaymentMethodValue.BankTransfer);

const normalizePaymentCategoryValue = (value: string | number): PaymentMethodCategoryValue =>
    enumNumber(value, paymentCategoryNameToValue, PaymentMethodCategoryValue.Participation);

export const getPaymentMethodsForCategory = (
    paymentMethods: PublicPaymentMethodOptions[] | undefined,
    category: PaymentMethodCategoryValue
): PaymentMethodValue[] => {
    const group = paymentMethods?.find(option => normalizePaymentCategoryValue(option.category) === category);
    return (group?.methods ?? []).map(normalizePaymentMethodValue);
};

export const formatEventDateRange = (form: PublicEventFormResponse, language: PublicRegistrationLanguage): string => {
    const formatter = new Intl.DateTimeFormat(language === "en" ? "en-US" : "hr-HR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

    const start = new Date(form.startDate);
    const end = new Date(form.endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return "";
    }

    return `${formatter.format(start)} - ${formatter.format(end)}`;
};

export const formatDateOnly = (value?: Dayjs | string | null): string | null => {
    if (!value) {
        return null;
    }

    if (dayjs.isDayjs(value)) {
        return value.format("YYYY-MM-DD");
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

export const serializeAnswerValue = (field: PublicFormField, rawValue: unknown): string => {
    const fieldType = normalizePublicFormFieldType(field.type);

    if (fieldType === "Checkbox") {
        return rawValue ? "true" : "false";
    }

    if (fieldType === "MultiSelect") {
        return Array.isArray(rawValue)
            ? rawValue.map(value => String(value).trim()).filter(Boolean).join(",")
            : trimValue(rawValue as string | undefined);
    }

    if (fieldType === "Date") {
        return formatDateOnly(rawValue as Dayjs | string | null) ?? "";
    }

    return rawValue === undefined || rawValue === null ? "" : String(rawValue).trim();
};

export const buildRoomAvailabilityOptionKey = (room: PublicRoomAvailability): string =>
    `${room.hotelId}:${room.roomTypeId}`;

export const findRoomAvailability = (
    rooms: PublicRoomAvailability[] | undefined,
    key?: string
): PublicRoomAvailability | undefined =>
    rooms?.find(room => buildRoomAvailabilityOptionKey(room) === key);

export const getDefaultAccommodationArrival = (room: PublicRoomAvailability): Dayjs | undefined => {
    const firstNight = room.nights?.[0]?.night;
    return firstNight ? dayjs(firstNight) : undefined;
};

export const getDefaultAccommodationDeparture = (room: PublicRoomAvailability): Dayjs | undefined => {
    const lastNight = room.nights?.[room.nights.length - 1]?.night;
    return lastNight ? dayjs(lastNight).add(1, "day") : undefined;
};

export const calculateNightCount = (arrival?: Dayjs | string | null, departure?: Dayjs | string | null): number => {
    const arrivalDate = arrival ? dayjs(arrival) : null;
    const departureDate = departure ? dayjs(departure) : null;

    if (!arrivalDate?.isValid() || !departureDate?.isValid()) {
        return 0;
    }

    return Math.max(0, departureDate.startOf("day").diff(arrivalDate.startOf("day"), "day"));
};

export const buildDynamicAnswerInitialValues = (fields: PublicFormField[] | undefined): Record<string, unknown> => {
    return (fields ?? []).reduce<Record<string, unknown>>((values, field) => {
        const fieldType = normalizePublicFormFieldType(field.type);
        if (fieldType === "Checkbox") {
            values[field.code] = false;
        } else if (fieldType === "MultiSelect") {
            values[field.code] = [];
        } else {
            values[field.code] = undefined;
        }
        return values;
    }, {});
};

export const buildInitialBulkRegistrationRowValues = (
    fields: PublicFormField[] | undefined,
    registrationOptions: PublicRegistrationOption[] | undefined = []
): PublicRegistrationBulkRowValues => ({
    answers: buildDynamicAnswerInitialValues(fields),
    selectedOptionIds: [],
    activityAnswers: buildActivityAnswerInitialValues(registrationOptions)
});

export const buildActivityAnswerInitialValues = (
    options: PublicRegistrationOption[] | undefined
): Record<string, unknown> => {
    return (options ?? []).reduce<Record<string, unknown>>((values, option) => {
        if (normalizePublicRegistrationOptionQuestionType(option.questionType) === "MultiChoice") {
            values[String(option.id)] = [];
        }

        return values;
    }, {});
};

export const buildInitialFormValues = (form: PublicEventFormResponse): PublicRegistrationFormValues => {
    const participationMethods = getPaymentMethodsForCategory(
        form.paymentMethods,
        PaymentMethodCategoryValue.Participation);
    const accommodationMethods = getPaymentMethodsForCategory(
        form.paymentMethods,
        PaymentMethodCategoryValue.Accommodation);
    const firstRoom = form.roomAvailability?.[0];

    return {
        payerType: PayerTypeValue.Individual,
        payerCountry: "HR",
        answers: buildDynamicAnswerInitialValues(form.template?.fields),
        activityAnswers: buildActivityAnswerInitialValues(form.registrationOptions),
        selectedParticipationFeePriceId: form.hasParticipationFee !== false
            ? form.participationFees?.[0]?.feePriceId
            : undefined,
        selectedOptionIds: [],
        participationPaymentMethod: participationMethods[0] ?? PaymentMethodValue.BankTransfer,
        reserveAccommodation: false,
        accommodationSelectionKey: firstRoom ? buildRoomAvailabilityOptionKey(firstRoom) : undefined,
        accommodationArrivalDate: firstRoom ? getDefaultAccommodationArrival(firstRoom) : undefined,
        accommodationDepartureDate: firstRoom ? getDefaultAccommodationDeparture(firstRoom) : undefined,
        accommodationRoomCount: 1,
        accommodationPaymentMethod: accommodationMethods[0] ?? PaymentMethodValue.BankTransfer,
        gdprConsentAccepted: false,
        bulkRows: [buildInitialBulkRegistrationRowValues(form.template?.fields, form.registrationOptions)]
    };
};

export const getActivePublicRegistrationChoiceIds = (option: PublicRegistrationOption): Set<number> =>
    new Set((option.choices ?? []).map(choice => Number(choice.id)).filter(choiceId => choiceId > 0));

// The activity fields the visibility rules need; public registration options and organizer event options
// both have them, so the public form and the participant page apply the same rules.
export type ActivityVisibilityOption = {
    id: number;
    allowedFeeTypeIds?: Array<number | string> | null;
    visibilityDependsOnEventOptionId?: number | null;
    visibilityDependsOnExpectedAnswer?: boolean | null;
};

const normalizeAllowedFeeTypeIds = (option: ActivityVisibilityOption): Set<number> =>
    new Set((option.allowedFeeTypeIds ?? [])
        .map(Number)
        .filter(feeTypeId => Number.isInteger(feeTypeId) && feeTypeId > 0));

const isOptionAvailableForFeeType = (option: ActivityVisibilityOption, feeTypeId: number | undefined): boolean => {
    const allowedFeeTypeIds = normalizeAllowedFeeTypeIds(option);
    if (allowedFeeTypeIds.size === 0) {
        return true;
    }

    return feeTypeId !== undefined && allowedFeeTypeIds.has(feeTypeId);
};

// Activities available for the chosen participation fee type whose visibility dependency is satisfied
// by the selected yes/no activities (same rule as EventOptionActivityAnswerResolver on the server).
export const filterActivityOptionsForFeeType = <T extends ActivityVisibilityOption>(
    options: T[] | undefined,
    feeTypeId: number | null | undefined,
    selectedOptionIds: Array<number | string> | undefined = []
): T[] => {
    const normalizedFeeTypeId = feeTypeId === null || feeTypeId === undefined ? undefined : Number(feeTypeId);
    const selectedYesNoOptionIds = new Set((selectedOptionIds ?? []).map(Number));
    const feeAvailableOptions = (options ?? [])
        .filter(option => isOptionAvailableForFeeType(option, normalizedFeeTypeId));
    const feeAvailableOptionsById = new Map(feeAvailableOptions.map(option => [option.id, option]));
    const isOptionVisible = (option: T, visiting: Set<number>): boolean => {
        const dependencyOptionId = Number(option.visibilityDependsOnEventOptionId);
        if (!Number.isInteger(dependencyOptionId) || dependencyOptionId <= 0) {
            return true;
        }

        if (dependencyOptionId === option.id || visiting.has(option.id)) {
            return false;
        }

        const sourceOption = feeAvailableOptionsById.get(dependencyOptionId);
        if (!sourceOption) {
            return false;
        }

        visiting.add(option.id);
        const sourceIsVisible = isOptionVisible(sourceOption, visiting);
        visiting.delete(option.id);
        if (!sourceIsVisible) {
            return false;
        }

        return selectedYesNoOptionIds.has(dependencyOptionId) === option.visibilityDependsOnExpectedAnswer;
    };

    return feeAvailableOptions.filter(option => isOptionVisible(option, new Set()));
};

export const filterPublicRegistrationOptionsForParticipationFee = (
    options: PublicRegistrationOption[] | undefined,
    participationFee: PublicParticipationFeeOption | undefined,
    selectedOptionIds: Array<number | string> | undefined = []
): PublicRegistrationOption[] =>
    filterActivityOptionsForFeeType(
        options,
        participationFee ? Number(participationFee.feeTypeId) : undefined,
        selectedOptionIds);

export const orderPublicRegistrationOptionsByDependency = <T extends ActivityVisibilityOption>(
    options: T[]
): T[] => {
    const optionIds = new Set(options.map(option => option.id));
    const childrenByParentId = new Map<number, T[]>();
    const ordered: T[] = [];
    const addedOptionIds = new Set<number>();

    options.forEach(option => {
        const parentId = Number(option.visibilityDependsOnEventOptionId);
        if (!Number.isInteger(parentId) || !optionIds.has(parentId)) {
            return;
        }

        const children = childrenByParentId.get(parentId) ?? [];
        children.push(option);
        childrenByParentId.set(parentId, children);
    });

    const appendOption = (option: T) => {
        if (addedOptionIds.has(option.id)) {
            return;
        }

        addedOptionIds.add(option.id);
        ordered.push(option);
        (childrenByParentId.get(option.id) ?? []).forEach(appendOption);
    };

    options
        .filter(option => {
            const parentId = Number(option.visibilityDependsOnEventOptionId);
            return !Number.isInteger(parentId) || !optionIds.has(parentId);
        })
        .forEach(appendOption);
    options.forEach(appendOption);

    return ordered;
};

export const paymentMethodLabel = (
    method: PaymentMethodValue,
    labels: Record<string, string>
): string => {
    switch (method) {
    case PaymentMethodValue.BankTransfer:
        return labels.bankTransfer;
    case PaymentMethodValue.Card:
        return labels.card;
    case PaymentMethodValue.Sponsor:
        return labels.sponsor;
    case PaymentMethodValue.Pos:
        return labels.pos;
    case PaymentMethodValue.Cash:
        return labels.cash;
    default:
        return String(method);
    }
};

export const formatBulkSubmitSummary = (
    template: string,
    accepted: number,
    rejected: number
): string => replaceLabelValue(
    replaceLabelValue(template, "accepted", String(accepted)),
    "rejected",
    String(rejected));
